import { useMemo, useState } from "react";
import type { Result } from "senf-atomic-design-system/src/components/special/survey/legacy/survey.types";
import { filterDbData } from "senf-shared";
import { prepareIdeaMapResults } from "../../../apps/senf-monitoring/src/util/geojsonHelpers";
import type { Project } from "../types";

interface IdeaTableColumn {
  value: string;
  label: string;
  type: string;
  values?: Array<{ value: string; label: string }>;
  withAI?: boolean;
}

interface IdeaMap {
  ideaTableColumns?: IdeaTableColumn[];
  comments: Array<Result & { ideaId: string }>;
  likes: Array<Result & { ideaId: string }>;
}

export const usePrepareData = (
  type: string,
  originalData: Result[],
  ideaMap: IdeaMap | undefined,
  project: Project,
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedArealFilter, setSelectedArealFilter] = useState<string | null>(
    null,
  );
  const [checkedSortOption, setCheckedSortOption] = useState("newest");
  const [mapBounds, setMapBounds] = useState<any>(null);

  const extraColumns = useMemo(
    () =>
      ideaMap?.ideaTableColumns?.map((column: IdeaTableColumn) => ({
        key: column.value,
        label: column.label,
        notSortable: true,
        columnId: column.value,
        columnTitle: column.label,
        columnType: column.type,
        value: column.value,
        values: column.values,
        withAI: column?.withAI,
      })) || [],
    [ideaMap?.ideaTableColumns],
  );

  const filteredData = useMemo(() => {
    if (!originalData) return [];

    const dbKeys = [
      "title",
      "body",
      "localityName",
      "address",
      "reason",
      "email",
    ];

    const preparedData = originalData;

    return filterDbData(
      type,
      preparedData,
      searchTerm,
      dbKeys,
      activeFilters,
      checkedSortOption,
      mapBounds,
      selectedArealFilter,
      project,
    );
  }, [
    originalData,
    type,
    searchTerm,
    activeFilters,
    checkedSortOption,
    mapBounds,
    selectedArealFilter,
    project,
  ]);

  const { visibleIdeas, visibleIdeasIds } = useMemo(() => {
    if (!ideaMap || !filteredData) {
      return { visibleIdeasIds: [], visibleIdeas: [] };
    }

    const result = {
      visibleIdeasIds: [] as string[],
      visibleIdeas: [] as Result[],
    };

    for (const idea of filteredData) {
      if (idea.isHidden) continue;

      let convertedTags = {};
      if (idea?.tags) {
        convertedTags = extraColumns.reduce<Record<string, string>>(
          (acc, extraCol) => {
            const matchingTag = idea.tags?.find(
              (tag: { key: string; value: string }) =>
                tag.key === extraCol.columnId,
            );
            const value =
              extraCol?.columnType !== "tags"
                ? matchingTag?.value
                : extraCol?.values?.find(
                    ({ value }) => value === matchingTag?.value,
                  )?.label;
            acc[extraCol.columnId] = value ?? " ";
            return acc;
          },
          {},
        );
      }

      result.visibleIdeasIds.push(idea.ideaId);
      result.visibleIdeas.push({
        ...idea,
        ...convertedTags,
      });
    }

    return result;
  }, [filteredData, ideaMap, extraColumns]);

  const filteredComments = useMemo(() => {
    if (!ideaMap?.comments) return [];
    return ideaMap.comments.filter((comment) =>
      visibleIdeasIds.includes(comment.ideaId),
    );
  }, [visibleIdeasIds, ideaMap?.comments]);

  const filteredLikes = useMemo(() => {
    if (!ideaMap?.likes) return [];
    return ideaMap.likes.filter((like) =>
      visibleIdeasIds.includes(like.ideaId),
    );
  }, [visibleIdeasIds, ideaMap?.likes]);

  const visibleIdeasFeatureCollection = useMemo(() => {
    return prepareIdeaMapResults(
      { ideas: visibleIdeas, ideaMap, project },
      true,
    );
  }, [visibleIdeas, ideaMap, project]);

  return {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setActiveFilters,
    selectedArealFilter,
    setSelectedArealFilter,
    mapBounds,
    setMapBounds,
    checkedSortOption,
    setCheckedSortOption,
    filteredLikes,
    filteredComments,
    extraColumns,
    filteredData,
    visibleIdeas,
    visibleIdeasFeatureCollection,
  };
};
